import { faCheck } from "@fortawesome/pro-solid-svg-icons/faCheck";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Checkbox,
  Divider,
  Flex,
  Group,
  Radio,
  Select,
  Stack,
  Stepper,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from "@mantine/core";
import {
  useDebouncedCallback,
  useDisclosure,
  usePrevious,
} from "@mantine/hooks";
import { nanoid } from "nanoid";
import { useParams } from "next/navigation";
import {
  useCallback,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
  useEffect,
} from "react";
import { twMerge } from "tailwind-merge";

import { QuestionWarningAlert } from "../question-warning-alert";
import { isVoiceResponseFormat } from "../utils";
import {
  AssessmentQuestionField,
  AssessmentQuestionFieldBaseLabel,
  AssessmentQuestionFieldLabel,
  FIELD_CONFIG,
  GenerateTooltip,
  getSharedInputClassNames,
  OTHER_FIELD_LABELS,
} from "./assessment-question-field";
import { CompetencyModalButtons } from "./competency-modal-buttons";
import {
  generateCompetencyFieldWithRetry,
  validateCompetencyStep,
} from "@/app/api/screener-builder";
import { ConfirmationPopover } from "@/components/ui/confirmation-popover";
import { useTrackAnalytics } from "@/hooks/analytics.hooks";
import {
  useCompetencyChangeTracking,
  useGenerateButtonStates,
} from "@/hooks/screener-builder-hooks";
import { useStateWithValidation } from "@/hooks/use-state-with-validation";
import { useGetCharCountValidation } from "@/hooks/validation";
import {
  useSaveCompetencyMutation,
  useUpdateCompetencyMutation,
} from "@/hooks/queries/screener-builder.queries";
import {
  AssessmentQuestionFields,
  AssessmentQuestionResponseFormat,
  type CompetencyModalLoadingFields,
  type FieldValidationState,
  type GenerateInputsMap,
  type GenerateResponse,
  isGenerateValidationError,
  type QuestionWarning,
  RESPONSE_FORMAT_LABELS,
  SCORING_RUBRIC,
  type SaveCompetencyResponse,
  type ReviewPageCompetency,
  SecondaryCompetencyModalLoadingFields,
} from "@/types/screener-builder";
import { AlertSeverity, Nullable, NullableValues } from "@/types/utils";
import { anyEmptyStrings } from "@/utils/helpers";
import {
  getPersistedWarning,
  MAX_COMPETENCIES_TOOLTIP_MESSAGE,
} from "@/utils/screener-builder";
import { trimStringValues } from "@/utils/string";
import {
  NotificationTypes,
  showUpwageNotification,
} from "@/utils/notifications";

export type CompetencyModalSaveMode = "remote" | "local";

// Continuous 1–10 minute range (AS-1868). Values are seconds.
const RECORDING_DURATION_OPTIONS = Array.from({ length: 10 }, (_, index) => {
  const minutes = index + 1;
  return {
    value: String(minutes * 60),
    label: `${minutes} ${minutes === 1 ? "minute" : "minutes"}`,
  };
});

// Show ~5 options before scrolling; keeps the dropdown height fixed so the
// modal layout doesn't shift as the option list grew (AS-1868).
const RECORDING_DURATION_MAX_DROPDOWN_HEIGHT = 200;

interface CompetencyModalContentProps {
  /** Whether the screener already has the maximum number of written
   *  assessment questions. Disables the Written option in the format picker
   *  so voice questions can still be added at the written cap. */
  atMaxWrittenCompetencies?: boolean;
  cancelConfirmationOpen: boolean;
  closeIconCloseConfirmation: () => void;
  closeCancelConfirmation: () => void;
  competency?: ReviewPageCompetency;
  defaultResponseFormat?: AssessmentQuestionResponseFormat;
  isPublishedScreener: boolean;
  jobDescription: string;
  jobTitle: string;
  onClose: () => void;
  onCompetencyUpdate: Dispatch<SetStateAction<ReviewPageCompetency[]>>;
  onUnsavedChangesChange: (hasChanges: boolean) => void;
  openCancelConfirmation: () => void;
  otherCompetencies: ReviewPageCompetency[];
  saveMode?: CompetencyModalSaveMode;
  /** Show the response-format picker (Written / Voice recommended / Voice
   *  required) when creating a new question — used by the unified
   *  Assessment Questions section. */
  showFormatPicker?: boolean;
}

// Mantine's default transition leaves out font color, the other properties are
// transitioned by default
const stepIconWithTranslationClassNames = (iconClassNames: string) =>
  twMerge(["transition-[background-color,border-color,color]", iconClassNames]);

const stepperStyles = {
  active: {
    stepDescription: "text-xs text-uw-gray-7",
    stepLabel: "font-semibold text-sm text-uw-gray-9",
    stepIcon: stepIconWithTranslationClassNames("text-sm text-uw-gray-9"),
  },
  inactive: {
    stepDescription: "text-xs text-uw-gray-7",
    stepLabel: "font-semibold text-sm text-uw-gray-7",
    stepIcon: stepIconWithTranslationClassNames(
      "bg-uw-gray-0 border-uw-gray-0 text-sm text-uw-gray-9",
    ),
  },
};

const RUBRIC_FIELD_MAX_CHARS = 800;

const DEFAULT_COMPETENCY: ReviewPageCompetency = {
  custom: true,
  definition: "",
  displayName: "",
  hasCustomScoringGuidelines: false,
  id: "NEW",
  isIncluded: true,
  name: "",
  questions: [],
  scoringGuidelines: {
    high: "",
    medium: "",
    low: "",
  },
};

const EMPTY_LLM_WARNING = { llmWarning: null, llmWarningField: null };
const VALIDATE_WARNING_FIELD = "validate";

// Helper function to assert response types in onGenerate - needed due to TypeScript's
// limitation with generic type narrowing in switch statements
const assertResponseType = <T extends AssessmentQuestionFields>(
  response: GenerateResponse<AssessmentQuestionFields>,
): GenerateResponse<T> => response as GenerateResponse<T>;

export const CompetencyModalContent = ({
  atMaxWrittenCompetencies = false,
  cancelConfirmationOpen,
  closeIconCloseConfirmation,
  closeCancelConfirmation,
  competency = DEFAULT_COMPETENCY,
  defaultResponseFormat,
  isPublishedScreener,
  jobDescription,
  jobTitle,
  onClose,
  onCompetencyUpdate,
  onUnsavedChangesChange,
  openCancelConfirmation,
  otherCompetencies,
  saveMode = "remote",
  showFormatPicker = false,
}: CompetencyModalContentProps) => {
  const { id: screenerId } = useParams<{ id?: string }>() ?? {};
  const isLocalSaveMode = saveMode === "local";
  const { mutate: saveCompetency, isPending: saveCompetencyPending } =
    useSaveCompetencyMutation(screenerId ?? "");
  const { mutate: updateCompetency, isPending: updateCompetencyPending } =
    useUpdateCompetencyMutation(screenerId ?? "");
  const isExistingCompetency = competency.id !== DEFAULT_COMPETENCY.id;

  const track = useTrackAnalytics();
  const debouncedTrack = useDebouncedCallback(track, 3000);
  /////////////////////////////
  // Steps and Loading State //
  /////////////////////////////
  const [activeStep, setActiveStep] = useState(0);
  const isStepOne = activeStep === 0;
  const [loadingField, setLoadingField] =
    useState<Nullable<CompetencyModalLoadingFields>>(null);
  const isEditingPublishedCompetency =
    isPublishedScreener && isExistingCompetency;
  const isLoading =
    !!loadingField ||
    (!isLocalSaveMode && (saveCompetencyPending || updateCompetencyPending));

  ////////////////////////////////////
  // Competency Configuration State //
  ////////////////////////////////////
  const [isCustomQuestion, setIsCustomQuestion] = useState(
    isLocalSaveMode && !isExistingCompetency ? true : competency.custom,
  );
  const [isCustomScoringRubric, setIsCustomScoringRubric] = useState(
    isLocalSaveMode ? true : competency.hasCustomScoringGuidelines,
  );
  const [hasCustomDisplayName, setHasCustomDisplayName] = useState(
    // Only the initial state is inferred, after initialization this is based
    // on the checked status of the custom display name checkbox
    competency.displayName !== competency.name,
  );
  const selectedQuestion = competency.questions.find((q) => q.isSelected);
  const initialResponseFormat =
    selectedQuestion?.responseFormat ??
    defaultResponseFormat ??
    AssessmentQuestionResponseFormat.Written;
  // Voice mode: modal was opened from the voice assessment section (new
  // question) or is editing an existing voice competency.
  const isVoiceMode = isVoiceResponseFormat(initialResponseFormat);
  // Full-format picker: shown for new questions in the unified Assessment
  // Questions section, where written and voice questions share one list and
  // the format is chosen inside the modal.
  const showFullFormatPicker = showFormatPicker && !isExistingCompetency;
  // In voice mode, responseFormat is editable (user picks VoiceRecommended or
  // VoiceRequired). In written mode it is read-only per AS-1756, unless the
  // full-format picker is active for a new question.
  const [responseFormat, setResponseFormat] =
    useState<AssessmentQuestionResponseFormat>(
      // With the picker shown and the written cap reached, Written is
      // disabled — default to a selectable voice format instead. AS-2019:
      // voice is always selectable now, so multilingual no longer factors in.
      showFullFormatPicker &&
        atMaxWrittenCompetencies &&
        initialResponseFormat === AssessmentQuestionResponseFormat.Written
        ? AssessmentQuestionResponseFormat.VoiceRecommended
        : initialResponseFormat,
    );
  const selectedFormatIsVoice = isVoiceResponseFormat(responseFormat);
  const [canSkip, setCanSkip] = useState(selectedQuestion?.canSkip ?? false);
  const [recordingDurationSeconds, setRecordingDurationSeconds] = useState(
    selectedQuestion?.recordingDurationSeconds ?? 60,
  );
  const [
    regenerateCheckPopoverOpen,
    { close: closeRegenerateCheckPopover, open: openRegenerateCheckPopover },
  ] = useDisclosure();
  const [
    regenerateRubricTooltipOpen,
    { close: closeRegenerateRubricTooltip, open: openRegenerateRubricTooltip },
  ] = useDisclosure();

  ///////////////////////
  // Competency Fields //
  ///////////////////////
  const [competencyName, setCompetencyName, { error: competencyNameError }] =
    useStateWithValidation<string, FieldValidationState>(
      competency.name,
      (name: string) => {
        const selfValidationResult =
          FIELD_CONFIG[AssessmentQuestionFields.Competency].validate(name);
        const nameIsUnique = !otherCompetencies.some(
          ({ name: otherCompetencyName }) =>
            otherCompetencyName.toLowerCase().trim() ===
            name.toLowerCase().trim(),
        );

        if (nameIsUnique) {
          return selfValidationResult;
        }

        const duplicateNameErrorText =
          "You already have a competency with this name";
        if (selfValidationResult.valid) {
          return {
            error: duplicateNameErrorText,
            valid: false,
            validationErrors: [duplicateNameErrorText],
          };
        }

        return {
          error: [selfValidationResult.error, duplicateNameErrorText]
            .filter(Boolean)
            .join("\n"),
          valid: false,
          validationErrors: selfValidationResult.validationErrors.concat(
            duplicateNameErrorText,
          ),
        };
      },
    );
  const [
    competencyDefinition,
    setCompetencyDefinition,
    { error: competencyDefinitionError },
  ] = useStateWithValidation<string, FieldValidationState>(
    competency.definition,
    FIELD_CONFIG[AssessmentQuestionFields.Definition].validate,
  );
  const [displayName, setDisplayName, { error: displayNameError }] =
    useStateWithValidation<string, FieldValidationState>(
      competency.displayName,
      // Display name follows the same rules as standard name
      FIELD_CONFIG[AssessmentQuestionFields.Competency].validate,
    );
  const [question, setQuestion] = useState(
    competency.questions.find(({ isSelected }) => isSelected)?.question ?? "",
  );
  const [questionOptions, setQuestionOptions] = useState(
    competency.questions ?? [],
  );
  const { error: questionError, warning: questionWarning } = useMemo(
    () => FIELD_CONFIG[AssessmentQuestionFields.Question].validate(question),
    [question],
  );
  const previousQuestionWarning = usePrevious(questionWarning);

  useEffect(() => {
    if (questionWarning && !previousQuestionWarning) {
      track("Yes/No Warning Triggered", { question });
    }
  }, [previousQuestionWarning, question, questionWarning, track]);

  ///////////////////////////
  // Scoring Rubric Fields //
  ///////////////////////////
  const [highScoringAnswer, setHighScoringAnswer, highScoringAnswerError] =
    useStateWithValidation(
      competency.scoringGuidelines.high,
      useGetCharCountValidation(RUBRIC_FIELD_MAX_CHARS),
    );
  const [lowScoringAnswer, setLowScoringAnswer, lowScoringAnswerError] =
    useStateWithValidation(
      competency.scoringGuidelines.low,
      useGetCharCountValidation(RUBRIC_FIELD_MAX_CHARS),
    );
  const [
    mediumScoringAnswer,
    setMediumScoringAnswer,
    mediumScoringAnswerError,
  ] = useStateWithValidation(
    competency.scoringGuidelines.medium,
    useGetCharCountValidation(RUBRIC_FIELD_MAX_CHARS),
  );
  const rubricFieldsHaveData = useMemo(
    () =>
      [highScoringAnswer, lowScoringAnswer, mediumScoringAnswer].some(
        (answer) => !anyEmptyStrings(answer),
      ),
    [highScoringAnswer, lowScoringAnswer, mediumScoringAnswer],
  );

  ////////////////
  // Validation //
  ////////////////
  const changeTracking = useCompetencyChangeTracking({
    competency,
    competencyName,
    competencyDefinition,
    question,
  });

  // Separate change tracking for modal close guardrails (tracks ALL modifications)
  const hasAnyChanges = useMemo(() => {
    const selectedQuestion =
      competency.questions.find((q) => q.isSelected)?.question ?? "";

    return (
      competencyName !== competency.name ||
      competencyDefinition !== competency.definition ||
      question !== selectedQuestion ||
      (hasCustomDisplayName && displayName !== competency.displayName) ||
      highScoringAnswer !== competency.scoringGuidelines.high ||
      mediumScoringAnswer !== competency.scoringGuidelines.medium ||
      lowScoringAnswer !== competency.scoringGuidelines.low ||
      responseFormat !==
        (competency.questions.find((q) => q.isSelected)?.responseFormat ??
          AssessmentQuestionResponseFormat.Written) ||
      canSkip !==
        (competency.questions.find((q) => q.isSelected)?.canSkip ?? false) ||
      recordingDurationSeconds !==
        (competency.questions.find((q) => q.isSelected)
          ?.recordingDurationSeconds ?? 60)
    );
  }, [
    competencyName,
    competency.name,
    competencyDefinition,
    competency.definition,
    question,
    competency.questions,
    hasCustomDisplayName,
    displayName,
    competency.displayName,
    highScoringAnswer,
    competency.scoringGuidelines.high,
    mediumScoringAnswer,
    competency.scoringGuidelines.medium,
    lowScoringAnswer,
    competency.scoringGuidelines.low,
    responseFormat,
    canSkip,
    recordingDurationSeconds,
  ]);

  // Keep the parent modal component updated to manage modal close guardrails
  useEffect(() => {
    onUnsavedChangesChange(hasAnyChanges);
  }, [hasAnyChanges, onUnsavedChangesChange]);

  const [
    { llmWarning: stepOneLLMWarning, llmWarningField: stepOneLLMWarningField },
    setLLMWarning,
  ] = useState<
    NullableValues<{
      llmWarning: QuestionWarning;
      llmWarningField: AssessmentQuestionFields | typeof VALIDATE_WARNING_FIELD;
    }>
  >(EMPTY_LLM_WARNING);
  const [rubricValidationError, setRubricValidationError] =
    useState<Nullable<QuestionWarning>>(null);
  const stepOneHasProgrammaticErrors = useMemo(
    () =>
      !!(
        competencyNameError ||
        competencyDefinitionError ||
        questionError ||
        (hasCustomDisplayName && displayNameError)
      ),
    [
      competencyDefinitionError,
      competencyNameError,
      displayNameError,
      hasCustomDisplayName,
      questionError,
    ],
  );
  const [
    { llmWarning: stepTwoLLMWarning, llmWarningField: stepTwoLLMWarningField },
    setStepTwoLLMWarning,
  ] = useState<
    NullableValues<{
      llmWarning: QuestionWarning;
      llmWarningField: AssessmentQuestionFields | typeof VALIDATE_WARNING_FIELD;
    }>
  >(EMPTY_LLM_WARNING);
  const stepTwoHasProgrammaticErrors = useMemo(
    () =>
      !!(
        highScoringAnswerError ||
        mediumScoringAnswerError ||
        lowScoringAnswerError
      ),
    [highScoringAnswerError, lowScoringAnswerError, mediumScoringAnswerError],
  );
  const getWarningForField = useCallback(
    (field: AssessmentQuestionFields) => {
      // Question is a special case as it can also have a programmatic warning
      // for Yes/No questions
      if (field === AssessmentQuestionFields.Question && questionWarning) {
        // If both programmatic and LLM warnings exist, pass them both through
        if (field === stepOneLLMWarningField && !!stepOneLLMWarning) {
          return Array.isArray(stepOneLLMWarning)
            ? [...stepOneLLMWarning, questionWarning]
            : [stepOneLLMWarning, questionWarning];
        }

        return questionWarning;
      }

      if (field === stepOneLLMWarningField && !!stepOneLLMWarning) {
        return stepOneLLMWarning;
      }
    },
    [questionWarning, stepOneLLMWarning, stepOneLLMWarningField],
  );
  const competencyPayload = useMemo(() => {
    const trimmedInputs = trimStringValues({
      competencyDefinition,
      competencyName,
      displayName: hasCustomDisplayName ? displayName : undefined,
      highScoringAnswerDescription: highScoringAnswer,
      lowScoringAnswerDescription: lowScoringAnswer,
      mediumScoringAnswerDescription: mediumScoringAnswer,
      question: isCustomQuestion ? question : undefined,
    });

    return {
      ...trimmedInputs,
      id: isExistingCompetency ? competency.id : undefined,
      isCustomScoringRubric: isCustomScoringRubric,
      isCustomQuestion: !!isCustomQuestion,
      question: isCustomQuestion
        ? // Safe to cast as validation will prevent us from reaching this point
          // if a custom question is empty
          (trimmedInputs.question as string)
        : questionOptions.map((option) => ({
            question: option.question,
            isIncluded: option.question === question,
          })),
      responseFormat,
      canSkip,
      recordingDurationSeconds,
      screenerId: screenerId ?? "",
      warnings: [stepOneLLMWarning?.message, stepTwoLLMWarning?.message]
        .filter(Boolean)
        .join("\n"),
    };
  }, [
    canSkip,
    competency.id,
    competencyDefinition,
    competencyName,
    displayName,
    hasCustomDisplayName,
    highScoringAnswer,
    isCustomQuestion,
    isCustomScoringRubric,
    isExistingCompetency,
    lowScoringAnswer,
    mediumScoringAnswer,
    question,
    questionOptions,
    recordingDurationSeconds,
    responseFormat,
    screenerId,
    stepOneLLMWarning,
    stepTwoLLMWarning,
  ]);

  ///////////////////
  // Button States //
  ///////////////////
  const nextDisabled = useMemo(() => {
    if (
      !!loadingField ||
      stepOneHasProgrammaticErrors ||
      anyEmptyStrings(competencyName, competencyDefinition, question)
    ) {
      return true;
    }

    return hasCustomDisplayName && anyEmptyStrings(displayName);
  }, [
    loadingField,
    stepOneHasProgrammaticErrors,
    competencyName,
    competencyDefinition,
    question,
    hasCustomDisplayName,
    displayName,
  ]);
  const saveDisabled = useMemo(
    () =>
      isLoading ||
      stepTwoHasProgrammaticErrors ||
      anyEmptyStrings(highScoringAnswer, mediumScoringAnswer, lowScoringAnswer),
    [
      highScoringAnswer,
      isLoading,
      lowScoringAnswer,
      mediumScoringAnswer,
      stepTwoHasProgrammaticErrors,
    ],
  );
  // We only include programmatic errors, as LLM warnings do not affect the
  // availability of generate buttons
  const generateButtonStates = useGenerateButtonStates({
    competencyName,
    competencyNameError,
    competencyDefinition,
    competencyDefinitionError,
    question,
    questionError,
  });

  ////////////////////
  // Event Handlers //
  ////////////////////
  const onDefinitionChange = useCallback(
    (value: string) => {
      debouncedTrack("Competency Definition Text Area Edited");
      setCompetencyDefinition(value);
    },
    [debouncedTrack, setCompetencyDefinition],
  );

  const onGenerate = useCallback(
    <T extends AssessmentQuestionFields>(field: T) => {
      const generateInput = generateButtonStates[field]
        .generateInput as GenerateInputsMap[T];

      if (generateInput) {
        if (field === AssessmentQuestionFields.Question) {
          if (generateButtonStates[field].isRegenerate && !isCustomQuestion) {
            // Product only wants to track question generation if a user is
            // regenerating non-custom questions.
            track("Question Regenerate Button Clicked");
          }
        } else {
          track(`${FIELD_CONFIG[field].analyticsName} Generate Button Clicked`);
        }

        setLoadingField(field);
        setLLMWarning(EMPTY_LLM_WARNING);

        generateCompetencyFieldWithRetry<T>(field, {
          components: {
            ...trimStringValues(generateInput),
            jobDescription,
            jobTitle,
          },
          prompt: field,
        })
          .then((response) => {
            switch (field) {
              case AssessmentQuestionFields.Competency:
                return setCompetencyName(
                  assertResponseType<AssessmentQuestionFields.Competency>(
                    response,
                  ).content,
                );
              case AssessmentQuestionFields.Definition:
                return setCompetencyDefinition(
                  assertResponseType<AssessmentQuestionFields.Definition>(
                    response,
                  ).content,
                );
              case AssessmentQuestionFields.Question: {
                setQuestion("");
                setIsCustomQuestion(false);
                const questionResponse =
                  assertResponseType<AssessmentQuestionFields.Question>(
                    response,
                  ).content;
                return setQuestionOptions(
                  questionResponse.map((q) => ({
                    questionId: q.id,
                    question: q.question,
                    isSelected: q.is_selected,
                  })),
                );
              }
            }
          })
          .catch((error) => {
            if (isGenerateValidationError(error)) {
              return setLLMWarning({
                llmWarning: {
                  message: error.response.data.warnings.join("\n\n"),
                  severity: AlertSeverity.Error,
                },
                llmWarningField: field,
              });
            }

            showUpwageNotification(
              "There was an issue communicating with the server. Please try again.",
              NotificationTypes.Error,
            );
          })
          .finally(() => setLoadingField(null));
      }
    },
    [
      generateButtonStates,
      isCustomQuestion,
      jobDescription,
      jobTitle,
      setCompetencyDefinition,
      setCompetencyName,
      track,
    ],
  );

  const onCustomQuestionToggle = useCallback(
    (checked: boolean) => {
      track("Use Custom Question Checkbox Toggled", { checked });
      setIsCustomQuestion(checked);
      setQuestion("");

      // Only trigger generation on custom question toggle if
      //   1. We don't already have generated questions in local state
      //   2. They are unchecking the toggle (turning custom off)
      if (questionOptions.length === 0 && !checked) {
        onGenerate(AssessmentQuestionFields.Question);
      }
    },
    [onGenerate, questionOptions.length, track],
  );

  const generateRubric = useCallback(() => {
    setStepTwoLLMWarning(EMPTY_LLM_WARNING);
    setLoadingField(SecondaryCompetencyModalLoadingFields.Rubric);
    setIsCustomScoringRubric(false);
    setHighScoringAnswer("");
    setMediumScoringAnswer("");
    setLowScoringAnswer("");

    return generateCompetencyFieldWithRetry<typeof SCORING_RUBRIC>(
      SCORING_RUBRIC,
      {
        prompt: SCORING_RUBRIC,
        components: {
          ...trimStringValues({
            competencyDefinition,
            competencyName,
            question,
          }),
          jobDescription,
          jobTitle,
        },
      },
    )
      .then(({ content: { high, low, medium } }) => {
        setHighScoringAnswer(high);
        setMediumScoringAnswer(medium);
        setLowScoringAnswer(low);
      })
      .catch((error) => {
        if (isGenerateValidationError(error)) {
          return setRubricValidationError({
            message: error.response.data.warnings.join("\n\n"),
            severity: AlertSeverity.Error,
          });
        }

        showUpwageNotification(
          "There was an issue communicating with the server. Please try again.",
          NotificationTypes.Error,
        );
      })
      .finally(() => setLoadingField(null));
  }, [
    competencyDefinition,
    competencyName,
    jobDescription,
    jobTitle,
    question,
    setHighScoringAnswer,
    setLowScoringAnswer,
    setMediumScoringAnswer,
  ]);

  const trackCustomRubricToggle = useCallback(
    (checked: boolean) =>
      track("Use Custom Scoring Rubric Checkbox Toggled", {
        checked,
      }),
    [track],
  );
  const onCustomRubricToggle = useCallback(
    (checked: boolean) => {
      const shouldShowTooltip = isCustomScoringRubric && rubricFieldsHaveData;
      if (!shouldShowTooltip) {
        trackCustomRubricToggle(checked);
      }

      if (shouldShowTooltip) {
        return openRegenerateRubricTooltip();
      }

      setIsCustomScoringRubric(checked);

      if (!checked) {
        generateRubric();
      }
    },
    [
      generateRubric,
      isCustomScoringRubric,
      openRegenerateRubricTooltip,
      rubricFieldsHaveData,
      trackCustomRubricToggle,
    ],
  );

  const onStepOneOverrideClick = useCallback(() => {
    if (nextDisabled) {
      return;
    }
    track("Step One Validation Override Clicked", {
      warnings: stepOneLLMWarning?.message,
    });

    setActiveStep(1);
    generateRubric();
  }, [generateRubric, nextDisabled, stepOneLLMWarning, track]);

  const onPublishedCompetencyNextClick = useCallback(() => {
    if (!changeTracking.needsValidation) {
      return setActiveStep(1);
    }

    openRegenerateCheckPopover();
  }, [changeTracking.needsValidation, openRegenerateCheckPopover]);

  const onNextClick = useCallback(
    (skipRubricGeneration = false) => {
      if (nextDisabled) {
        return;
      }

      if (isLocalSaveMode) {
        setActiveStep(1);
        return;
      }

      if (!changeTracking.needsValidation) {
        return setActiveStep(1);
      }

      setLoadingField(SecondaryCompetencyModalLoadingFields.Validation);
      validateCompetencyStep({
        step: 1,
        ...trimStringValues({
          competencyDefinition,
          competencyName,
          question,
        }),
        jobDescription,
        jobTitle,
      })
        .then(({ validated, warning }) => {
          if (validated) {
            setLLMWarning(EMPTY_LLM_WARNING);
            changeTracking.markValidationSuccess();
            setActiveStep(1);

            if (skipRubricGeneration) {
              setLoadingField(null);
              return;
            }

            return generateRubric();
          }

          track("Step One Final Check Validation Error", { warnings: warning });

          setLoadingField(null);
          changeTracking.markValidationFailure();
          setLLMWarning({
            llmWarning: {
              message: warning.join("\n\n"),
              severity: AlertSeverity.Error,
            },
            llmWarningField: VALIDATE_WARNING_FIELD,
          });
        })
        .catch(() => {
          setLoadingField(null);
          // Mark validation as failed on network errors too
          changeTracking.markValidationFailure();
          showUpwageNotification(
            "There was an issue communicating with the server. Please try again.",
            NotificationTypes.Error,
          );
        });
    },
    [
      changeTracking,
      competencyDefinition,
      competencyName,
      generateRubric,
      isLocalSaveMode,
      jobDescription,
      jobTitle,
      nextDisabled,
      question,
      track,
    ],
  );

  const performSave = useCallback(() => {
    const applySavedCompetency = (
      {
        canSkip: savedCanSkip,
        competencyDefinition,
        competencyName,
        displayName,
        highScoringAnswerDescription,
        id,
        isCustomScoringRubric: hasCustomScoringGuidelines,
        isCustomQuestion,
        lowScoringAnswerDescription,
        mediumScoringAnswerDescription,
        question,
        recordingDurationSeconds: savedRecordingDurationSeconds,
        responseFormat: savedResponseFormat,
        warnings,
      }: SaveCompetencyResponse,
      showSuccessNotification = true,
    ) => {
      const savedFormat = savedResponseFormat ?? responseFormat;
      const savedSkip = savedCanSkip ?? canSkip;
      const updatedCompetency: ReviewPageCompetency = {
        custom: isCustomQuestion,
        definition: competencyDefinition,
        displayName: displayName,
        hasCustomScoringGuidelines,
        id: id,
        // When editing a competency, leave the existing inclusion status in
        // place. New competencies are included by default.
        isIncluded: competency?.isIncluded ?? true,
        name: competencyName,
        questions: Array.isArray(question)
          ? question.map(({ isIncluded, ...rest }) => ({
              ...rest,
              isSelected: !!isIncluded,
              responseFormat: savedFormat,
              canSkip: savedSkip,
              recordingDurationSeconds: savedRecordingDurationSeconds,
            }))
          : [
              {
                isSelected: !!question.isIncluded,
                question: question.question,
                questionId: question.questionId,
                responseFormat: savedFormat,
                canSkip: savedSkip,
                recordingDurationSeconds: savedRecordingDurationSeconds,
              },
            ],
        scoringGuidelines: {
          high: highScoringAnswerDescription,
          low: lowScoringAnswerDescription,
          medium: mediumScoringAnswerDescription,
        },
        warningBypassLog: (() => {
          const existingLog = competency?.warningBypassLog ?? [];
          const now = new Date().toISOString();
          const resolvedLog = existingLog.map((entry) =>
            entry.resolvedAt ? entry : { ...entry, resolvedAt: now },
          );

          if (warnings) {
            return [
              ...resolvedLog,
              // We use a stub here for optimistic updating, the full model
              // will be populated on the next fetch/page load. At time of
              // implementation FE only reads warningMessage and resolvedAt
              {
                warningMessage: warnings,
                timestamp: "",
                component: [] as string[],
                warningType: "general",
                userId: "",
                userEmail: "",
                resolvedAt: null,
              },
            ];
          }

          return resolvedLog.length > 0 ? resolvedLog : null;
        })(),
      };

      if (isExistingCompetency) {
        onCompetencyUpdate((prev) =>
          prev.map((comp) =>
            comp.id === competency.id ? updatedCompetency : comp,
          ),
        );
      } else {
        onCompetencyUpdate((prev) => [...prev, updatedCompetency]);
      }

      if (showSuccessNotification) {
        showUpwageNotification(
          `Competency ${isExistingCompetency ? "updated" : "saved"} successfully`,
          NotificationTypes.Success,
        );
      }
      onClose();
    };

    const onSuccess = (response: SaveCompetencyResponse) => {
      applySavedCompetency(response);
    };

    const onError = () => {
      showUpwageNotification(
        `There was an issue ${isExistingCompetency ? "updating" : "saving"} the competency. Please try again.`,
        NotificationTypes.Error,
      );
    };

    if (isLocalSaveMode) {
      const localQuestion: SaveCompetencyResponse["question"] = Array.isArray(
        competencyPayload.question,
      )
        ? competencyPayload.question.map((questionOption) => {
            const questionId =
              "questionId" in questionOption &&
              typeof questionOption.questionId === "string"
                ? questionOption.questionId
                : nanoid();

            return {
              isIncluded: !!questionOption.isIncluded,
              question: questionOption.question,
              questionId,
            };
          })
        : {
            isIncluded: true,
            question: competencyPayload.question,
            questionId: selectedQuestion?.questionId ?? nanoid(),
          };

      applySavedCompetency(
        {
          canSkip: competencyPayload.canSkip,
          competencyDefinition: competencyPayload.competencyDefinition,
          competencyName: competencyPayload.competencyName,
          displayName:
            competencyPayload.displayName ?? competencyPayload.competencyName,
          highScoringAnswerDescription:
            competencyPayload.highScoringAnswerDescription,
          id: competencyPayload.id ?? nanoid(),
          isCustomQuestion: competencyPayload.isCustomQuestion,
          isCustomScoringRubric: competencyPayload.isCustomScoringRubric,
          lowScoringAnswerDescription:
            competencyPayload.lowScoringAnswerDescription,
          mediumScoringAnswerDescription:
            competencyPayload.mediumScoringAnswerDescription,
          question: localQuestion,
          recordingDurationSeconds: competencyPayload.recordingDurationSeconds,
          responseFormat: competencyPayload.responseFormat,
          screenerId: competencyPayload.screenerId,
          warnings: competencyPayload.warnings || null,
        },
        false,
      );
      return;
    }

    if (isExistingCompetency) {
      updateCompetency(
        { competencyId: competency.id, payload: competencyPayload },
        { onSuccess, onError },
      );
    } else {
      saveCompetency(competencyPayload, { onSuccess, onError });
    }
  }, [
    canSkip,
    competency.id,
    competency.isIncluded,
    competency.warningBypassLog,
    competencyPayload,
    isExistingCompetency,
    isLocalSaveMode,
    onClose,
    onCompetencyUpdate,
    responseFormat,
    saveCompetency,
    selectedQuestion?.questionId,
    updateCompetency,
  ]);

  const onSaveClick = useCallback(() => {
    if (saveDisabled) {
      return;
    }

    if (isLocalSaveMode) {
      performSave();
      return;
    }

    setLoadingField(SecondaryCompetencyModalLoadingFields.Validation);
    validateCompetencyStep({
      ...trimStringValues({
        competencyDefinition,
        competencyName,
        highScoringRubric: highScoringAnswer,
        lowScoringRubric: lowScoringAnswer,
        mediumScoringRubric: mediumScoringAnswer,
        question,
      }),
      jobDescription,
      jobTitle,
      step: 2,
    })
      .then(({ validated, warning }) => {
        if (validated) {
          setStepTwoLLMWarning(EMPTY_LLM_WARNING);
          performSave();
        } else {
          track("Step Two Final Check Validation Error", { warnings: warning });

          changeTracking.markValidationFailure();
          setStepTwoLLMWarning({
            llmWarning: {
              message: warning.join("\n\n"),
              severity: AlertSeverity.Error,
            },
            llmWarningField: VALIDATE_WARNING_FIELD,
          });
        }
      })
      .catch(() => {
        // Mark validation as failed on network errors too
        changeTracking.markValidationFailure();
        showUpwageNotification(
          "There was an issue communicating with the server. Please try again.",
          NotificationTypes.Error,
        );
      })
      .finally(() => setLoadingField(null));
  }, [
    changeTracking,
    competencyDefinition,
    competencyName,
    highScoringAnswer,
    isLocalSaveMode,
    jobDescription,
    jobTitle,
    lowScoringAnswer,
    mediumScoringAnswer,
    performSave,
    question,
    saveDisabled,
    track,
  ]);

  const onStepTwoOverrideClick = useCallback(() => {
    if (saveDisabled) {
      return;
    }
    track("Step Two Validation Override Clicked", {
      warnings: stepTwoLLMWarning?.message,
    });

    performSave();
  }, [performSave, saveDisabled, stepTwoLLMWarning, track]);

  const persistedWarning = useMemo(
    () => getPersistedWarning(competency.warningBypassLog),
    [competency.warningBypassLog],
  );

  return (
    <>
      <Divider mb="24" />
      <Stepper
        active={activeStep}
        classNames={{
          separator: "bg-uw-gray-0 flex-initial w-[72px]",
        }}
      >
        <Stepper.Step
          className={twMerge([
            !isStepOne && "opacity-50",
            "transition-opacity",
          ])}
          classNames={
            isStepOne
              ? stepperStyles.active
              : {
                  ...stepperStyles.inactive,
                  stepIcon: stepIconWithTranslationClassNames(
                    "bg-uw-purple text-sm text-white",
                  ),
                }
          }
          completedIcon={<FontAwesomeIcon icon={faCheck} size="xl" />}
          description="Question setup"
          label="Step 1"
          loading={
            isStepOne &&
            loadingField === SecondaryCompetencyModalLoadingFields.Validation
          }
        />
        <Stepper.Step
          className={twMerge([isStepOne && "opacity-50", "transition-opacity"])}
          classNames={isStepOne ? stepperStyles.inactive : stepperStyles.active}
          description="Scoring rubric"
          label="Step 2"
          loading={
            loadingField === SecondaryCompetencyModalLoadingFields.Rubric ||
            (!isStepOne &&
              loadingField === SecondaryCompetencyModalLoadingFields.Validation)
          }
        />
      </Stepper>

      <Divider my="24" />

      {activeStep === 0 ? (
        <Stack gap={18}>
          <AssessmentQuestionField
            error={competencyNameError}
            field={AssessmentQuestionFields.Competency}
            generateButtonState={
              generateButtonStates[AssessmentQuestionFields.Competency]
            }
            loading={isLoading}
            onBlur={setCompetencyName}
            onChange={setCompetencyName}
            onGenerate={() => onGenerate(AssessmentQuestionFields.Competency)}
            showGenerateButton={!isLocalSaveMode}
            value={competencyName}
            warning={getWarningForField(AssessmentQuestionFields.Competency)}
          />

          <Checkbox
            checked={hasCustomDisplayName}
            disabled={isLoading}
            label={OTHER_FIELD_LABELS.customDisplayName}
            onChange={({ currentTarget: { checked } }) => {
              track("Use Custom Display Name Checkbox", { checked });
              setHasCustomDisplayName(checked);
              setDisplayName(checked ? competencyName : "");
            }}
          />

          {hasCustomDisplayName && (
            <Flex direction="column">
              <TextInput
                classNames={getSharedInputClassNames(false)}
                disabled={isLoading}
                error={displayNameError}
                label={
                  <AssessmentQuestionFieldBaseLabel
                    labelText="Competency display name"
                    helpText="Name used for this competency in the Talent Hub and on the interview PDF"
                  />
                }
                onBlur={(event) =>
                  setDisplayName(event.currentTarget.value.trim())
                }
                onChange={(event) => setDisplayName(event.currentTarget.value)}
                value={displayName}
              />
            </Flex>
          )}

          <AssessmentQuestionField
            error={competencyDefinitionError}
            field={AssessmentQuestionFields.Definition}
            generateButtonState={
              generateButtonStates[AssessmentQuestionFields.Definition]
            }
            loading={isLoading}
            onBlur={setCompetencyDefinition}
            onChange={onDefinitionChange}
            onGenerate={() => onGenerate(AssessmentQuestionFields.Definition)}
            showGenerateButton={!isLocalSaveMode}
            value={competencyDefinition}
            warning={getWarningForField(AssessmentQuestionFields.Definition)}
          />

          {isCustomQuestion ? (
            <AssessmentQuestionField
              error={questionError}
              field={AssessmentQuestionFields.Question}
              generateButtonState={
                generateButtonStates[AssessmentQuestionFields.Question]
              }
              loading={isLoading}
              onBlur={setQuestion}
              onChange={setQuestion}
              onGenerate={() => onGenerate(AssessmentQuestionFields.Question)}
              showGenerateButton={!isLocalSaveMode}
              value={question}
              warning={getWarningForField(AssessmentQuestionFields.Question)}
            />
          ) : (
            <>
              <Radio.Group
                classNames={{
                  label: "mb-0.5 w-full",
                }}
                label={
                  <AssessmentQuestionFieldLabel
                    onGenerate={() =>
                      onGenerate(AssessmentQuestionFields.Question)
                    }
                    generateButtonState={{
                      ...generateButtonStates[
                        AssessmentQuestionFields.Question
                      ],
                      isRegenerate: true,
                    }}
                    loading={isLoading}
                    field={AssessmentQuestionFields.Question}
                    helpLink={
                      FIELD_CONFIG[AssessmentQuestionFields.Question].helpLink
                    }
                    labelText={
                      FIELD_CONFIG[AssessmentQuestionFields.Question].labelText
                    }
                    showGenerateButton={!isLocalSaveMode}
                  />
                }
                onChange={(question) => {
                  track("Question Selected", { question });
                  setQuestion(question);
                }}
                value={question}
              >
                <Group gap={6}>
                  {loadingField === AssessmentQuestionFields.Question
                    ? new Array(5)
                        .fill("Generating...")
                        .map((content, index) => (
                          <Radio
                            className="border border-uw-gray-2 rounded-md px-3.5 py-3 w-full"
                            disabled
                            key={index}
                            label={content}
                          />
                        ))
                    : questionOptions.map(({ question: questionOption }) => (
                        <Radio
                          className="border border-uw-gray-2 rounded-md px-3.5 py-3 w-full"
                          disabled={isLoading}
                          key={questionOption}
                          label={questionOption}
                          value={questionOption}
                        />
                      ))}
                </Group>
              </Radio.Group>
              <QuestionWarningAlert
                warning={getWarningForField(AssessmentQuestionFields.Question)}
              />
            </>
          )}

          {!isLocalSaveMode && (
            <Checkbox
              checked={isCustomQuestion}
              disabled={
                isLoading ||
                (isCustomQuestion &&
                  generateButtonStates[AssessmentQuestionFields.Question]
                    .disabled)
              }
              label={OTHER_FIELD_LABELS.customQuestion}
              onChange={({ currentTarget: { checked } }) =>
                onCustomQuestionToggle(checked)
              }
            />
          )}

          {/* Response format toggle removed per AS-1756 for written questions.
              For voice questions (opened from the voice section), show the
              voice format options so users can choose between VoiceRecommended
              and VoiceRequired. New questions in the unified Assessment
              Questions section get the full picker including Written. */}
          {(isVoiceMode || showFullFormatPicker) && (
            <Flex direction="column" gap="xs">
              <Text className="text-sm font-medium text-uw-gray-9">
                Response format
              </Text>
              <Radio.Group
                onChange={(value) => {
                  const format = value as AssessmentQuestionResponseFormat;
                  setResponseFormat(format);
                  // Skipping only applies to voice questions
                  if (format === AssessmentQuestionResponseFormat.Written) {
                    setCanSkip(false);
                  }
                }}
                value={responseFormat}
              >
                <Flex gap="md">
                  {(showFullFormatPicker
                    ? [
                        AssessmentQuestionResponseFormat.Written,
                        AssessmentQuestionResponseFormat.VoiceRecommended,
                        AssessmentQuestionResponseFormat.VoiceRequired,
                      ]
                    : [
                        AssessmentQuestionResponseFormat.VoiceRecommended,
                        AssessmentQuestionResponseFormat.VoiceRequired,
                      ]
                  ).map((format) => {
                    const isWrittenFormat =
                      format === AssessmentQuestionResponseFormat.Written;
                    // AS-2019: voice formats are selectable on multilingual
                    // screeners now, so Written's cap is the only remaining
                    // reason a format card is disabled.
                    const formatDisabled =
                      isLoading ||
                      (isWrittenFormat && atMaxWrittenCompetencies);
                    const disabledTooltip =
                      isWrittenFormat && atMaxWrittenCompetencies
                        ? MAX_COMPETENCIES_TOOLTIP_MESSAGE
                        : null;

                    const card = (
                      <Radio.Card
                        className={twMerge([
                          "flex-1",
                          "p-4",
                          "border",
                          responseFormat === format
                            ? "border-uw-blue-5 bg-uw-blue-0"
                            : "border-uw-gray-2 bg-white",
                          formatDisabled && "cursor-not-allowed opacity-60",
                        ])}
                        disabled={formatDisabled}
                        key={format}
                        radius="sm"
                        value={format}
                      >
                        <Group align="center" gap="sm" wrap="nowrap">
                          <Radio.Indicator />
                          <Text className="text-sm text-uw-gray-9">
                            {RESPONSE_FORMAT_LABELS[format]}
                          </Text>
                        </Group>
                      </Radio.Card>
                    );

                    if (disabledTooltip) {
                      return (
                        <Tooltip
                          key={format}
                          label={disabledTooltip}
                          multiline
                          w={250}
                          withArrow
                        >
                          {card}
                        </Tooltip>
                      );
                    }

                    return card;
                  })}
                </Flex>
              </Radio.Group>
            </Flex>
          )}

          {selectedFormatIsVoice && (
            <>
              <Select
                allowDeselect={false}
                data={RECORDING_DURATION_OPTIONS}
                disabled={isLoading}
                label={
                  <Text className="text-sm font-medium text-uw-gray-9">
                    Recording duration
                  </Text>
                }
                maxDropdownHeight={RECORDING_DURATION_MAX_DROPDOWN_HEIGHT}
                onChange={(value) => setRecordingDurationSeconds(Number(value))}
                value={String(recordingDurationSeconds)}
              />

              <Checkbox
                checked={canSkip}
                disabled={isLoading}
                label="Allow candidate to skip this question"
                onChange={({ currentTarget: { checked } }) =>
                  setCanSkip(checked)
                }
              />
            </>
          )}
        </Stack>
      ) : (
        <Stack gap={18}>
          <QuestionWarningAlert
            warning={
              rubricValidationError
                ? {
                    message:
                      rubricValidationError.message +
                      "\n\nYou can fix the errors in Step 1 or write a custom scoring rubric to continue.",
                    severity: AlertSeverity.Error,
                  }
                : isLocalSaveMode
                  ? {
                      message:
                        "Write the scoring rubric that should be copied with this default assessment question.",
                      severity: AlertSeverity.Info,
                    }
                  : {
                      message:
                        "We recommend using our AI-generated rubric, which has been fine-tuned to score candidates for this competency and role.",
                      severity: AlertSeverity.Info,
                    }
            }
          />
          <Textarea
            autosize
            maxRows={9}
            minRows={3}
            classNames={getSharedInputClassNames()}
            disabled={!isCustomScoringRubric}
            error={highScoringAnswerError}
            label={OTHER_FIELD_LABELS.highScoringAnswer}
            onBlur={(event) =>
              setHighScoringAnswer(event.currentTarget.value.trim())
            }
            onChange={(event) =>
              setHighScoringAnswer(event.currentTarget.value)
            }
            required
            value={highScoringAnswer}
          />

          <Textarea
            autosize
            maxRows={9}
            minRows={3}
            classNames={getSharedInputClassNames()}
            disabled={!isCustomScoringRubric}
            error={mediumScoringAnswerError}
            label={OTHER_FIELD_LABELS.mediumScoringAnswer}
            onBlur={(event) =>
              setMediumScoringAnswer(event.currentTarget.value.trim())
            }
            onChange={(event) =>
              setMediumScoringAnswer(event.currentTarget.value)
            }
            required
            value={mediumScoringAnswer}
          />

          <Textarea
            autosize
            maxRows={9}
            minRows={3}
            classNames={getSharedInputClassNames()}
            disabled={!isCustomScoringRubric}
            error={lowScoringAnswerError}
            label={OTHER_FIELD_LABELS.lowScoringAnswer}
            onBlur={(event) =>
              setLowScoringAnswer(event.currentTarget.value.trim())
            }
            onChange={(event) => setLowScoringAnswer(event.currentTarget.value)}
            required
            value={lowScoringAnswer}
          />

          {!isLocalSaveMode && (
            <GenerateTooltip
              close={closeRegenerateRubricTooltip}
              field={"scoring rubric"}
              generateButtonState={{
                disabled: isLoading,
                isRegenerate: rubricFieldsHaveData,
                tooltip: "",
              }}
              isOpen={regenerateRubricTooltipOpen}
              loading={isLoading}
              onGenerate={() => {
                trackCustomRubricToggle(false);
                generateRubric();
              }}
            >
              <Checkbox
                checked={isCustomScoringRubric}
                disabled={isLoading}
                label={OTHER_FIELD_LABELS.customRubric}
                onChange={({ currentTarget: { checked } }) =>
                  onCustomRubricToggle(checked)
                }
              />
            </GenerateTooltip>
          )}
        </Stack>
      )}
      {persistedWarning && (
        <div className="mt-[18px]">
          <QuestionWarningAlert warning={persistedWarning} />
        </div>
      )}
      <Divider my="24" />
      <CompetencyModalButtons
        cancelConfirmationOpen={cancelConfirmationOpen}
        closeIconCloseConfirmation={closeIconCloseConfirmation}
        closeCancelConfirmation={closeCancelConfirmation}
        competency={competencyPayload}
        getPrimaryButtonWrapper={
          isStepOne && isEditingPublishedCompetency
            ? (children) => (
                <ConfirmationPopover
                  cancelButtonContent="Continue without regenerating"
                  isOpen={regenerateCheckPopoverOpen}
                  message="Do you want to automatically regenerate a scoring rubric with the updated information?"
                  onCancelButtonClick={() => onNextClick(true)}
                  onClose={closeRegenerateCheckPopover}
                  onPrimaryButtonClick={onNextClick}
                  primaryButtonContent="Regenerate"
                  wide
                >
                  {children}
                </ConfirmationPopover>
              )
            : undefined
        }
        hasUnsavedChanges={hasAnyChanges}
        isExistingCompetency={isExistingCompetency}
        isPublishedScreener={isPublishedScreener}
        isStepOne={isStepOne}
        llmWarning={
          (isStepOne &&
            stepOneLLMWarningField === VALIDATE_WARNING_FIELD &&
            stepOneLLMWarning) ||
          (!isStepOne &&
            stepTwoLLMWarningField === VALIDATE_WARNING_FIELD &&
            stepTwoLLMWarning) ||
          null
        }
        loadingField={loadingField}
        onBackClick={() => {
          setActiveStep(0);
          setRubricValidationError(null);
        }}
        onCancelClick={onClose}
        onDangerButtonClick={
          isStepOne ? onStepOneOverrideClick : onStepTwoOverrideClick
        }
        onPrimaryButtonClick={
          isStepOne
            ? isEditingPublishedCompetency
              ? onPublishedCompetencyNextClick
              : () => onNextClick()
            : onSaveClick
        }
        openCancelConfirmation={openCancelConfirmation}
        primaryButtonDisabled={isStepOne ? nextDisabled : saveDisabled}
        stepHasProgrammaticErrors={
          isStepOne
            ? stepOneHasProgrammaticErrors
            : stepTwoHasProgrammaticErrors
        }
      />
    </>
  );
};
